import {useEffect} from 'react'
import {useAddBanner, useDismissBanner, type InitialBanner} from '@github-ui/banner-stack'
import {useAnalytics} from '@github-ui/use-analytics'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Link} from '@primer/react'
import type {StatelessS2sTokenFormatChangeBannerPayload} from '../../routes/workflows-layout-route'

export const STATELESS_S2S_TOKEN_FORMAT_CHANGE_BANNER_ID = 'stateless-s2s-token-format-change-banner'

const CHANGELOG_URL =
  'https://github.blog/changelog/2026-05-15-github-app-installation-tokens-per-request-override-header'

type SendAnalyticsEvent = ReturnType<typeof useAnalytics>['sendAnalyticsEvent']
type StatelessS2sTokenFormatChangeBanner = InitialBanner & {'data-test-selector'?: string}

interface StatelessS2sTokenFormatChangeBannerOptions extends StatelessS2sTokenFormatChangeBannerPayload {
  sendAnalyticsEvent: SendAnalyticsEvent
  onDismissed?: () => void
}

function sendBannerAnalytics(sendAnalyticsEvent: SendAnalyticsEvent, action: 'dismiss' | 'open_changelog') {
  sendAnalyticsEvent('analytics.click', undefined, {
    category: 'ACTIONS_WORKFLOWS',
    action: `stateless_s2s_token_format_change_banner.${action}`,
    label: `ref_cta:${action};ref_loc:actions_workflow_banner`,
  })
}

/** Builds the stateless GitHub App installation-token format notice for BannerStack. */
export function getStatelessS2sTokenFormatChangeBanner({
  dismissPath,
  sendAnalyticsEvent,
  onDismissed,
}: StatelessS2sTokenFormatChangeBannerOptions): StatelessS2sTokenFormatChangeBanner {
  return {
    id: STATELESS_S2S_TOKEN_FORMAT_CHANGE_BANNER_ID,
    title: 'Upcoming change to GitHub App installation token format',
    description: (
      <>
        <span>
          GitHub App installation tokens will soon use a new stateless format (ghs_...) and may be longer (~520
          characters). Apps with hardcoded length assumptions may break.
        </span>
        <br />
        <br />
        <span>
          Validate your apps and workflows with the per-request override header detailed in this{' '}
          <Link inline href={CHANGELOG_URL} onClick={() => sendBannerAnalytics(sendAnalyticsEvent, 'open_changelog')}>
            GitHub Changelog
          </Link>
          .
        </span>
      </>
    ),
    variant: 'info',
    dismissible: true,
    persistOnNavigation: true,
    onDismiss: async () => {
      sendBannerAnalytics(sendAnalyticsEvent, 'dismiss')
      const response = await verifiedFetch(dismissPath, {method: 'POST'})
      if (!response.ok) {
        throw new Error(`Failed to dismiss the stateless S2S token format change banner (${response.status})`)
      }
      onDismissed?.()
    },
    'data-test-selector': STATELESS_S2S_TOKEN_FORMAT_CHANGE_BANNER_ID,
  }
}

/** Keeps the stateless token-format notice synchronized with route payload changes. */
export function useStatelessS2sTokenFormatChangeBanner({
  data,
  onDismissed,
}: {
  data?: StatelessS2sTokenFormatChangeBannerPayload
  onDismissed?: () => void
}) {
  const addBanner = useAddBanner()
  const dismissBanner = useDismissBanner()
  const {sendAnalyticsEvent} = useAnalytics({optional: true})

  useEffect(() => {
    if (!data?.dismissPath) {
      dismissBanner(STATELESS_S2S_TOKEN_FORMAT_CHANGE_BANNER_ID)
      return
    }

    const id = addBanner(getStatelessS2sTokenFormatChangeBanner({...data, sendAnalyticsEvent, onDismissed}))

    return () => {
      dismissBanner(id)
    }
  }, [data, addBanner, dismissBanner, onDismissed, sendAnalyticsEvent])
}
